import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL || 'https://hbbemkrgnbrsivhvosdj.supabase.co';

function generateRawKey() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return 'wlcrt_' + Array.from(array)?.map(b => b?.toString(16)?.padStart(2, '0'))?.join('');
}

async function hashKey(rawKey) {
  const encoder = new TextEncoder();
  const data = encoder?.encode(rawKey);
  const hashBuffer = await crypto.subtle?.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray?.map(b => b?.toString(16)?.padStart(2, '0'))?.join('');
}

// Business type → field configuration
const BUSINESS_CONFIGS = {
  moving: {
    label: 'Moving Company',
    icon: 'Truck',
    color: 'blue',
    description: 'Optimized for moving quotes — includes pickup & delivery addresses.',
    fields: [
      { name: 'name', type: 'text', placeholder: 'Full Name', required: true, label: 'Full Name' },
      { name: 'phone', type: 'tel', placeholder: 'Phone Number', required: true, label: 'Phone Number' },
      { name: 'email', type: 'email', placeholder: 'Email Address', required: false, label: 'Email Address' },
      { name: 'pickup_address', type: 'text', placeholder: 'Pickup Address', required: true, label: 'Pickup Address' },
      { name: 'delivery_address', type: 'text', placeholder: 'Delivery Address', required: true, label: 'Delivery Address' },
      { name: 'service_type', type: 'select', placeholder: 'Service Type', required: false, label: 'Service Type', options: ['Local Move', 'Long Distance', 'Commercial Move', 'Storage', 'Packing Only'] },
      { name: 'notes', type: 'textarea', placeholder: 'Additional details (move date, special items, etc.)', required: false, label: 'Notes' },
    ],
  },
  handyman: {
    label: 'Handyman',
    icon: 'Wrench',
    color: 'orange',
    description: 'Optimized for service requests — includes task description and urgency.',
    fields: [
      { name: 'name', type: 'text', placeholder: 'Full Name', required: true, label: 'Full Name' },
      { name: 'phone', type: 'tel', placeholder: 'Phone Number', required: true, label: 'Phone Number' },
      { name: 'email', type: 'email', placeholder: 'Email Address', required: false, label: 'Email Address' },
      { name: 'pickup_address', type: 'text', placeholder: 'Service Address', required: false, label: 'Service Address' },
      { name: 'service_type', type: 'select', placeholder: 'Type of Work', required: false, label: 'Type of Work', options: ['Plumbing', 'Electrical', 'Carpentry', 'Painting', 'General Repair', 'Other'] },
      { name: 'notes', type: 'textarea', placeholder: 'Describe the task and urgency level', required: false, label: 'Task Description & Urgency' },
    ],
  },
  cleaning: {
    label: 'Cleaning Service',
    icon: 'Sparkles',
    color: 'green',
    description: 'Optimized for cleaning bookings — includes address and service type.',
    fields: [
      { name: 'name', type: 'text', placeholder: 'Full Name', required: true, label: 'Full Name' },
      { name: 'phone', type: 'tel', placeholder: 'Phone Number', required: true, label: 'Phone Number' },
      { name: 'email', type: 'email', placeholder: 'Email Address', required: false, label: 'Email Address' },
      { name: 'pickup_address', type: 'text', placeholder: 'Property Address', required: false, label: 'Property Address' },
      { name: 'service_type', type: 'select', placeholder: 'Cleaning Type', required: false, label: 'Cleaning Type', options: ['Standard Cleaning', 'Deep Cleaning', 'Move-In/Out', 'Office Cleaning', 'Post-Construction'] },
      { name: 'notes', type: 'textarea', placeholder: 'Property size, special instructions, preferred schedule', required: false, label: 'Notes' },
    ],
  },
  roofing: {
    label: 'Roofing',
    icon: 'Home',
    color: 'red',
    description: 'Optimized for roofing estimates — includes property address and service type.',
    fields: [
      { name: 'name', type: 'text', placeholder: 'Full Name', required: true, label: 'Full Name' },
      { name: 'phone', type: 'tel', placeholder: 'Phone Number', required: true, label: 'Phone Number' },
      { name: 'email', type: 'email', placeholder: 'Email Address', required: false, label: 'Email Address' },
      { name: 'pickup_address', type: 'text', placeholder: 'Property Address', required: false, label: 'Property Address' },
      { name: 'service_type', type: 'select', placeholder: 'Service Needed', required: false, label: 'Service Needed', options: ['Roof Repair', 'Full Replacement', 'Inspection', 'Gutters', 'Emergency Repair'] },
      { name: 'notes', type: 'textarea', placeholder: 'Describe the issue or project', required: false, label: 'Notes' },
    ],
  },
  landscaping: {
    label: 'Landscaping',
    icon: 'Trees',
    color: 'emerald',
    description: 'Optimized for landscaping quotes — includes property address and service type.',
    fields: [
      { name: 'name', type: 'text', placeholder: 'Full Name', required: true, label: 'Full Name' },
      { name: 'phone', type: 'tel', placeholder: 'Phone Number', required: true, label: 'Phone Number' },
      { name: 'email', type: 'email', placeholder: 'Email Address', required: false, label: 'Email Address' },
      { name: 'pickup_address', type: 'text', placeholder: 'Property Address', required: false, label: 'Property Address' },
      { name: 'service_type', type: 'select', placeholder: 'Service Type', required: false, label: 'Service Type', options: ['Lawn Mowing', 'Landscaping Design', 'Tree Trimming', 'Irrigation', 'Snow Removal'] },
      { name: 'notes', type: 'textarea', placeholder: 'Property size, project details, preferred schedule', required: false, label: 'Notes' },
    ],
  },
  mobile_detailing: {
    label: 'Mobile Detailing',
    icon: 'Car',
    color: 'cyan',
    description: 'Optimized for detailing bookings — includes vehicle info and service location.',
    fields: [
      { name: 'name', type: 'text', placeholder: 'Full Name', required: true, label: 'Full Name' },
      { name: 'phone', type: 'tel', placeholder: 'Phone Number', required: true, label: 'Phone Number' },
      { name: 'email', type: 'email', placeholder: 'Email Address', required: false, label: 'Email Address' },
      { name: 'service_type', type: 'select', placeholder: 'Service Type', required: true, label: 'Service Type', options: ['Basic Wash & Vacuum', 'Full Interior Detail', 'Full Exterior Detail', 'Full Detail (Interior + Exterior)', 'Paint Correction', 'Ceramic Coating', 'Paint Protection Film (PPF)', 'Headlight Restoration', 'Engine Bay Cleaning', 'Odor Elimination'] },
      { name: 'vehicle_type', type: 'select', placeholder: 'Vehicle Type', required: false, label: 'Vehicle Type', options: ['Sedan', 'SUV / Crossover', 'Truck', 'Van / Minivan', 'Sports Car', 'Luxury / Exotic', 'Motorcycle', 'Boat / RV', 'Fleet Vehicle'] },
      { name: 'pickup_address', type: 'text', placeholder: 'Service Location (we come to you)', required: false, label: 'Service Location' },
      { name: 'notes', type: 'textarea', placeholder: 'Vehicle make/model/year, paint condition, special requests', required: false, label: 'Vehicle Details & Notes' },
    ],
  },
  default: {
    label: 'General Business',
    icon: 'Building2',
    color: 'purple',
    description: 'Universal form with all available fields.',
    fields: [
      { name: 'name', type: 'text', placeholder: 'Full Name', required: true, label: 'Full Name' },
      { name: 'phone', type: 'tel', placeholder: 'Phone Number', required: true, label: 'Phone Number' },
      { name: 'email', type: 'email', placeholder: 'Email Address', required: false, label: 'Email Address' },
      { name: 'pickup_address', type: 'text', placeholder: 'Address / Location', required: false, label: 'Address / Location' },
      { name: 'service_type', type: 'select', placeholder: 'Service Type', required: false, label: 'Service Type', options: ['General Inquiry', 'Quote Request', 'Service Request', 'Other'] },
      { name: 'notes', type: 'textarea', placeholder: 'Additional information or message', required: false, label: 'Message / Notes' },
    ],
  },
};

function getBusinessConfig(businessType) {
  if (!businessType) return BUSINESS_CONFIGS?.default;
  const key = businessType?.toLowerCase()?.replace(/\s+/g, '_');
  return BUSINESS_CONFIGS?.[key] || BUSINESS_CONFIGS?.default;
}

function buildFieldHtml(field) {
  const style = `display:block;width:100%;margin-bottom:10px;padding:10px;border:1px solid #ccc;border-radius:6px;font-size:14px`;
  if (field?.type === 'select') {
    const opts = field?.options?.map(o => `    <option value="${o}">${o}</option>`)?.join('\n') || '';
    return `  <select name="${field?.name}" style="${style}">
    <option value="">-- ${field?.placeholder} --</option>
${opts}
  </select>`;
  }
  if (field?.type === 'textarea') {
    return `  <textarea name="${field?.name}" placeholder="${field?.placeholder}" rows="3" style="${style}"></textarea>`;
  }
  return `  <input name="${field?.name}" type="${field?.type}" placeholder="${field?.placeholder}"${field?.required ? ' required' : ''} style="${style}" />`;
}

function buildJsonBody(fields) {
  const lines = fields?.map(f => {
    if (f?.type === 'select' || f?.type === 'textarea') {
      return `          ${f?.name}: form.${f?.name}.value`;
    }
    return `          ${f?.name}: form.${f?.name}.value`;
  });
  return lines?.join(',\n');
}

function generateSnippet(apiKey, fields) {
  const formFields = fields?.map(buildFieldHtml)?.join('\n');
  const jsonBody = buildJsonBody(fields);

  return `<!-- Wilcart CRM Lead Form -->
<script>
async function submitToWilcart(event) {
  event.preventDefault();
  const form = event.target;
  const btn = form.querySelector('button[type="submit"]');
  btn.disabled = true;
  btn.textContent = 'Sending...';

  try {
    const res = await fetch(
      '${SUPABASE_URL}/functions/v1/public-leads-api',
      {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ${apiKey || 'YOUR_API_KEY_HERE'}',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
${jsonBody},
          source: 'website'
        })
      }
    );
    const data = await res.json();
    if (data.success) {
      form.innerHTML = '<p style="color:green;font-weight:bold">✅ Thank you! We will contact you shortly.</p>';
    } else {
      btn.disabled = false;
      btn.textContent = 'Submit';
      alert('Error: ' + (data.error || 'Please try again.'));
    }
  } catch(e) {
    btn.disabled = false;
    btn.textContent = 'Submit';
    alert('Network error. Please try again.');
  }
}
</script>

<form onSubmit="submitToWilcart(event)" style="max-width:420px">
${formFields}
  <button type="submit" style="width:100%;padding:12px;background:#4F46E5;color:#fff;border:none;border-radius:6px;font-size:16px;cursor:pointer">
    Get a Free Quote
  </button>
</form>`;
}

function generateWordpressSnippet(apiKey, fields) {
  const fieldSelectors = fields?.map(f => {
    if (f?.name === 'name') return `      var name  = form.querySelector('[name*="name"], [name*="your-name"]');`;
    if (f?.name === 'email') return `      var email = form.querySelector('[name*="email"], [name*="your-email"]');`;
    if (f?.name === 'phone') return `      var phone = form.querySelector('[name*="phone"], [name*="tel"]');`;
    return `      var ${f?.name} = form.querySelector('[name*="${f?.name}"]');`;
  })?.join('\n');

  const bodyFields = fields?.map(f => {
    if (f?.name === 'name') return `          name:  name  ? name.value  : ''`;
    if (f?.name === 'email') return `          email: email ? email.value : ''`;
    if (f?.name === 'phone') return `          phone: phone ? phone.value : ''`;
    return `          ${f?.name}: ${f?.name} ? ${f?.name}.value : ''`;
  })?.join(',\n');

  return `// Add to your theme's functions.php or a custom plugin:

add_action('wp_footer', function() { ?>
<script>
document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('form.wpcf7-form, form[id*="contact"]').forEach(function(form) {
    form.addEventListener('submit', function(e) {
${fieldSelectors}
      if (!name) return;
      fetch('${SUPABASE_URL}/functions/v1/public-leads-api', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ${apiKey || 'YOUR_API_KEY_HERE'}',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
${bodyFields},
          source: 'wordpress'
        })
      });
    });
  });
});
</script>
<?php });`;
}

const Step = ({ number, title, description, children, done }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 flex flex-col items-center">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all
        ${done ? 'bg-green-500 border-green-500 text-white' : 'bg-background border-primary text-primary'}`}>
        {done ? <Icon name="Check" size={16} /> : number}
      </div>
      {number < 4 && <div className="w-0.5 flex-1 bg-border mt-2 min-h-[24px]" />}
    </div>
    <div className="pb-8 flex-1 min-w-0">
      <h4 className="font-semibold text-card-foreground mb-1">{title}</h4>
      <p className="text-sm text-muted-foreground mb-3">{description}</p>
      {children}
    </div>
  </div>
);

const FIELD_LABELS = {
  name: 'Name',
  phone: 'Phone',
  email: 'Email',
  pickup_address: 'Pickup / Service Address',
  delivery_address: 'Delivery Address',
  service_type: 'Service Type',
  notes: 'Notes / Message',
};

const WebsiteIntegrationTab = () => {
  const { organization, profile } = useAuth();
  const [apiKey, setApiKey] = useState(null);
  const [newKeyRaw, setNewKeyRaw] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [lastLead, setLastLead] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('html');

  const isAdmin = profile?.role === 'admin';
  const displayKey = newKeyRaw || apiKey?.display || null;

  // Detect business config from org
  const businessConfig = getBusinessConfig(organization?.business_type);
  const activeFields = businessConfig?.fields;

  const codeSnippet = generateSnippet(displayKey, activeFields);
  const wordpressSnippet = generateWordpressSnippet(displayKey, activeFields);

  const fetchKeyAndStatus = async () => {
    if (!organization?.id) return;
    setLoadingStatus(true);
    try {
      const { data: keys } = await supabase?.from('api_keys')?.select('id, name, created_at')?.eq('org_id', organization?.id)?.order('created_at', { ascending: false })?.limit(1);
      if (keys?.length > 0) setApiKey(keys?.[0]);

      const { data: leads } = await supabase?.from('leads')?.select('id, name, created_at, source')?.eq('org_id', organization?.id)?.ilike('source', '%api%')?.order('created_at', { ascending: false })?.limit(1);
      if (leads?.length > 0) setLastLead(leads?.[0]);
    } catch (err) {
      // silent
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    fetchKeyAndStatus();
  }, [organization?.id]);

  const handleGenerateKey = async () => {
    if (!isAdmin || !organization?.id) return;
    setGenerating(true);
    setError(null);
    setNewKeyRaw(null);
    try {
      const rawKey = generateRawKey();
      const hash = await hashKey(rawKey);
      const { error: insertError } = await supabase?.from('api_keys')?.insert({
        org_id: organization?.id,
        key_hash: hash,
        name: 'Website Integration Key',
      });
      if (insertError) throw insertError;
      setNewKeyRaw(rawKey);
      await fetchKeyAndStatus();
    } catch (err) {
      setError(err?.message || 'Failed to generate key.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async (text, id) => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 2500);
    } catch { /* fallback */ }
  };

  const handleTestConnection = async () => {
    if (!displayKey) {
      setTestResult({ success: false, message: 'Generate an API key first (Step 1).' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/public-leads-api`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${displayKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: 'Test Lead (Integration Check)',
          phone: '+10000000000',
          email: 'test@example.com',
          pickup_address: '123 Test St, City, State',
          service_type: 'Test',
          notes: 'Automatically generated test lead from Settings → Website Integration.',
          source: 'website',
        }),
      });
      const data = await res?.json();
      if (data?.success) {
        setTestResult({ success: true, message: '✅ Connection successful! A test lead was created in your CRM.' });
        await fetchKeyAndStatus();
      } else {
        setTestResult({ success: false, message: `❌ ${data?.error || 'Connection failed. Check your API key.'}` });
      }
    } catch (err) {
      setTestResult({ success: false, message: `❌ Network error: ${err?.message}` });
    } finally {
      setTesting(false);
    }
  };

  const hasKey = !!apiKey || !!newKeyRaw;
  const hasLead = !!lastLead;

  const colorMap = {
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    orange: 'bg-orange-100 text-orange-700 border-orange-200',
    green: 'bg-green-100 text-green-700 border-green-200',
    red: 'bg-red-100 text-red-700 border-red-200',
    emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
  };
  const badgeClass = colorMap?.[businessConfig?.color] || colorMap?.purple;

  return (
    <div className="space-y-8">
      {/* Hero banner */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-primary/15 rounded-xl flex items-center justify-center flex-shrink-0">
            <Icon name="Globe" size={24} className="text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-card-foreground mb-1">Connect Your Website in 4 Steps</h3>
            <p className="text-sm text-muted-foreground">
              No developer needed. Copy, paste, done. Every form submission on your site will automatically appear as a new lead in your CRM — with all the fields your business needs.
            </p>
          </div>
        </div>
      </div>
      {/* Business type badge */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${badgeClass}`}>
        <Icon name={businessConfig?.icon} size={18} />
        <div>
          <p className="text-sm font-semibold">Form auto-configured for: {businessConfig?.label}</p>
          <p className="text-xs opacity-80">{businessConfig?.description}</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {activeFields?.map(f => (
            <span key={f?.name} className="text-xs bg-white/60 border border-current/20 rounded-full px-2 py-0.5 font-medium opacity-90">
              {FIELD_LABELS?.[f?.name] || f?.name}
            </span>
          ))}
        </div>
      </div>
      {/* Live status */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border flex-1 ${hasKey ? 'bg-green-50 border-green-200' : 'bg-muted border-border'}`}>
          <div className={`w-2.5 h-2.5 rounded-full ${hasKey ? 'bg-green-500 animate-pulse' : 'bg-muted-foreground'}`} />
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">API Key</p>
            <p className={`text-sm font-semibold ${hasKey ? 'text-green-700' : 'text-card-foreground'}`}>
              {hasKey ? 'Generated ✓' : 'Not generated'}
            </p>
          </div>
        </div>
        <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border flex-1 ${hasLead ? 'bg-green-50 border-green-200' : 'bg-muted border-border'}`}>
          <div className={`w-2.5 h-2.5 rounded-full ${hasLead ? 'bg-green-500' : 'bg-muted-foreground'}`} />
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Last Lead via API</p>
            <p className={`text-sm font-semibold ${hasLead ? 'text-green-700' : 'text-card-foreground'}`}>
              {loadingStatus ? '...' : hasLead ? new Date(lastLead.created_at)?.toLocaleString() : 'No leads yet'}
            </p>
          </div>
        </div>
        <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border flex-1 ${hasLead ? 'bg-green-50 border-green-200' : 'bg-muted border-border'}`}>
          <Icon name={hasLead ? 'CheckCircle2' : 'Clock'} size={18} className={hasLead ? 'text-green-600' : 'text-muted-foreground'} />
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Integration Status</p>
            <p className={`text-sm font-semibold ${hasLead ? 'text-green-700' : 'text-card-foreground'}`}>
              {hasLead ? 'Active & Working' : 'Pending Setup'}
            </p>
          </div>
        </div>
      </div>
      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          {error}
        </div>
      )}
      {/* Steps */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-base font-bold text-card-foreground mb-6 flex items-center gap-2">
          <Icon name="ListChecks" size={18} className="text-primary" />
          Setup Guide
        </h3>

        {/* Step 1 */}
        <Step number={1} title="Generate Your API Key" description="Your API key is a unique password that links form submissions to your CRM account." done={hasKey}>
          {newKeyRaw ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
              <div className="flex items-center gap-2 text-amber-800 font-medium text-sm">
                <Icon name="AlertTriangle" size={16} />
                Save this key now — it will not be shown again!
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white border border-amber-200 rounded px-3 py-2 text-xs font-mono text-amber-900 break-all">
                  {newKeyRaw}
                </code>
                <button
                  onClick={() => handleCopy(newKeyRaw, 'newkey')}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-smooth"
                >
                  <Icon name={copied === 'newkey' ? 'Check' : 'Copy'} size={14} />
                  {copied === 'newkey' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-xs text-amber-700">✅ Key saved. It's already pre-filled in the code snippet below.</p>
            </div>
          ) : hasKey ? (
            <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <Icon name="CheckCircle2" size={18} className="text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800">API key is ready</p>
                <p className="text-xs text-green-700">Created on {new Date(apiKey?.created_at)?.toLocaleDateString()}. The key is pre-filled in the code snippet below.</p>
              </div>
              {isAdmin && (
                <button
                  onClick={handleGenerateKey}
                  disabled={generating}
                  className="ml-auto text-xs text-muted-foreground hover:text-foreground underline"
                >
                  Regenerate
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={handleGenerateKey}
              disabled={generating || !isAdmin}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth disabled:opacity-60"
            >
              <Icon name={generating ? 'Loader2' : 'Key'} size={16} className={generating ? 'animate-spin' : ''} />
              {generating ? 'Generating...' : 'Generate API Key'}
            </button>
          )}
          {!isAdmin && !hasKey && (
            <p className="text-xs text-muted-foreground mt-2">Only admins can generate API keys.</p>
          )}
        </Step>

        {/* Step 2 */}
        <Step number={2} title="Copy the Code Snippet" description="This ready-made form is pre-configured for your business type. Your API key is already filled in — just copy and paste." done={false}>
          <div className="space-y-3">
            {/* Tab switcher */}
            <div className="flex gap-1 bg-muted rounded-lg p-1 w-fit">
              <button
                onClick={() => setActiveTab('html')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-smooth ${activeTab === 'html' ? 'bg-background text-card-foreground shadow-sm' : 'text-muted-foreground hover:text-card-foreground'}`}
              >
                HTML Form
              </button>
              <button
                onClick={() => setActiveTab('wordpress')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-smooth ${activeTab === 'wordpress' ? 'bg-background text-card-foreground shadow-sm' : 'text-muted-foreground hover:text-card-foreground'}`}
              >
                WordPress
              </button>
            </div>

            <div className="relative">
              <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-xs overflow-x-auto max-h-72 leading-relaxed font-mono whitespace-pre-wrap break-all">
                {activeTab === 'html' ? codeSnippet : wordpressSnippet}
              </pre>
              <button
                onClick={() => handleCopy(activeTab === 'html' ? codeSnippet : wordpressSnippet, 'snippet')}
                className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-md text-xs font-medium transition-smooth"
              >
                <Icon name={copied === 'snippet' ? 'Check' : 'Copy'} size={13} />
                {copied === 'snippet' ? 'Copied!' : 'Copy Code'}
              </button>
            </div>

            {/* Fields legend */}
            <div className="p-3 bg-muted rounded-lg border border-border">
              <p className="text-xs font-semibold text-card-foreground mb-2 flex items-center gap-1.5">
                <Icon name="Info" size={13} className="text-primary" />
                Fields included in this snippet:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {activeFields?.map(f => (
                  <span key={f?.name} className={`text-xs rounded-full px-2.5 py-0.5 font-medium border ${f?.required ? 'bg-primary/10 text-primary border-primary/20' : 'bg-muted-foreground/10 text-muted-foreground border-border'}`}>
                    {FIELD_LABELS?.[f?.name] || f?.name}{f?.required ? ' *' : ''}
                  </span>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">* Required field</p>
            </div>

            {!displayKey && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <Icon name="AlertCircle" size={13} />
                Complete Step 1 first — your API key will be auto-filled in the code.
              </p>
            )}
          </div>
        </Step>

        {/* Step 3 */}
        <Step number={3} title="Paste on Your Website" description="Add the code to any page on your website where you want a contact form." done={false}>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="p-4 bg-muted rounded-lg border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="FileCode2" size={16} className="text-primary" />
                <span className="text-sm font-semibold text-card-foreground">Plain HTML Website</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Open your page's HTML file. Paste the code anywhere inside the <code className="bg-background px-1 rounded">&lt;body&gt;</code> tag where you want the form to appear.
              </p>
            </div>
            <div className="p-4 bg-muted rounded-lg border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Globe" size={16} className="text-primary" />
                <span className="text-sm font-semibold text-card-foreground">Website Builder (Wix, Tilda, etc.)</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Look for "Custom Code", "HTML Block", or "Embed" widget. Paste the code there and publish.
              </p>
            </div>
          </div>
        </Step>

        {/* Step 4 */}
        <Step number={4} title="Test the Connection" description="Click the button below to send a test lead and confirm everything is working." done={hasLead}>
          <div className="space-y-3">
            <button
              onClick={handleTestConnection}
              disabled={testing}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth disabled:opacity-60"
            >
              <Icon name={testing ? 'Loader2' : 'Zap'} size={16} className={testing ? 'animate-spin' : ''} />
              {testing ? 'Testing...' : 'Send Test Lead'}
            </button>
            {testResult && (
              <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${testResult?.success ? 'bg-green-50 border-green-200 text-green-800' : 'bg-destructive/10 border-destructive/20 text-destructive'}`}>
                <Icon name={testResult?.success ? 'CheckCircle2' : 'XCircle'} size={16} className="mt-0.5 flex-shrink-0" />
                {testResult?.message}
              </div>
            )}
          </div>
        </Step>
      </div>
      {/* API Reference */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
            <Icon name="Code2" size={20} className="text-slate-600" />
          </div>
          <div>
            <h4 className="font-semibold text-card-foreground">All Available Fields</h4>
            <p className="text-xs text-muted-foreground">You can send any combination of these fields from your custom form</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-4 text-muted-foreground font-medium">Field</th>
                <th className="text-left py-2 pr-4 text-muted-foreground font-medium">Type</th>
                <th className="text-left py-2 pr-4 text-muted-foreground font-medium">Required</th>
                <th className="text-left py-2 text-muted-foreground font-medium">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                { field: 'name', type: 'string', req: 'Yes', desc: 'Full name of the lead' },
                { field: 'phone', type: 'string', req: 'Recommended', desc: 'Phone number (any format)' },
                { field: 'email', type: 'string', req: 'No', desc: 'Email address' },
                { field: 'pickup_address', type: 'string', req: 'No', desc: 'Pickup, service, or property address' },
                { field: 'delivery_address', type: 'string', req: 'No', desc: 'Delivery or destination address (moving)' },
                { field: 'service_type', type: 'string', req: 'No', desc: 'Type of service requested' },
                { field: 'notes', type: 'string', req: 'No', desc: 'Additional message, task description, or details' },
                { field: 'source', type: 'string', req: 'No', desc: 'Lead source tag (e.g. "website", "landing-page")' },
              ]?.map(row => (
                <tr key={row?.field}>
                  <td className="py-2 pr-4 font-mono text-primary">{row?.field}</td>
                  <td className="py-2 pr-4 text-muted-foreground">{row?.type}</td>
                  <td className="py-2 pr-4">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${row?.req === 'Yes' ? 'bg-red-100 text-red-700' : row?.req === 'Recommended' ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'}`}>
                      {row?.req}
                    </span>
                  </td>
                  <td className="py-2 text-muted-foreground">{row?.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* FAQ */}
      <div className="bg-muted rounded-xl p-6 space-y-4">
        <h4 className="font-semibold text-card-foreground flex items-center gap-2">
          <Icon name="HelpCircle" size={18} className="text-primary" />
          Frequently Asked Questions
        </h4>
        <div className="space-y-3">
          {[
            {
              q: 'Where do the leads appear in the CRM?',
              a: 'Every form submission appears instantly in the Leads section, tagged with source "website (API)" so you can filter them easily.'
            },
            {
              q: 'Why does the form look different for each business type?',
              a: 'The snippet is auto-configured based on your business type set during onboarding. A moving company needs pickup/delivery addresses; a handyman needs task description. This means new clients can self-serve without any manual setup.'
            },
            {
              q: 'Is it safe to put the API key on my website?',
              a: 'Yes. The key only has permission to create new leads — it cannot read, edit, or delete any data. Even if someone finds it, they can only submit leads.'
            },
            {
              q: 'What if I need to revoke the key?',
              a: 'Go to Settings → API Keys, click "Revoke" next to the key, then generate a new one and update your website code.'
            },
            {
              q: 'Which fields are required?',
              a: 'Only "name" is required. You should also provide at least a phone number or email. All other fields are optional but recommended for your business type.'
            },
          ]?.map((item, i) => (
            <div key={i} className="bg-background border border-border rounded-lg p-4">
              <p className="text-sm font-medium text-card-foreground mb-1">{item?.q}</p>
              <p className="text-sm text-muted-foreground">{item?.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WebsiteIntegrationTab;
